import * as React from 'react';

import * as Images from 'pos/Images';
import { CustomerForEditionDTO, Res, Permission, CustomerOfferDTO, CustomerDocumentIdType } from 'core/api';
import { AcceptActionButton, BackActionButton, Content, Filler, Footer, Header, LargeDeviceOnly, PhoneOnly, PosScreen } from 'pos/ui';
import { CountryNames, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import showEditCustomerOfferDialog from './showEditCustomerOfferDialog';
import { CustomerFacade } from 'pos/facades';
import { Command } from 'pos/common';
import { Message, Modal } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { Box, HStack, SegmentGroup, VStack } from '@chakra-ui/react';
import { BasicButton } from 'core/ui-next';
import { isPhone } from 'pos/ui/ThemeManager';
import {
	administrativePanel,
	AdministrativePanelParams,
	commercialTermsPanel,
	contactPanel,
	customerFormSchema,
	CustomerFormState,
	locationPanel,
	notesPanel,
	promotionsPanel,
	promotionsPhonePanel,
	PromotionsPanelParams,
	toCustomerForEdition,
	toCustomerFormState
} from './customerCRUDpannels';

type Props = {
	customer: CustomerForEditionDTO;
} & Modal.Actions<CustomerForEditionDTO>;


// Sólo se puede usar el tipo de documento "Otros" si el usuario tiene el permiso correspondiente
async function userCanSetDocumentIdType(customer: CustomerForEditionDTO): Promise<boolean> {
	if (customer.DocumentIdType === CustomerDocumentIdType.Other &&
		SessionProvider.hasPermission(Permission.Customer_CreateOrModifyWithOtherDocumentIdType) === false) {
		await Message.show(Res.Msg.MSG_USER_HAS_NO_PERMISSION_TO_CREATE_CUSTOMER_WITH_OTHER_DOCUMENT_ID_TYPE());
		return false;
	}
	return true;
}

// Antes de guardar avisamos al usuario si el cliente que está creando o editando se parece a otros que ya existen
async function checkDuplicates(customer: CustomerForEditionDTO): Promise<boolean> {
	const duplicates = await CustomerFacade.getPossibleDuplicates(customer);
	if (duplicates.length === 0)
		return true;

	const info = duplicates.map(d => `- ${d.Id}: ${d.FiscalName}`).join('\r\n');

	const result = await Message.confirm(Res.Msg.MSG_THERE_ARE_POSSIBLE_DUPLICATES_CUSTOMER_DO_YOU_WANT_TO_CONTINUE(info));
	return result.ok;
}


function AddOrEditCustomerScreen(props: Props) {

	const { customer, cancel, close } = props;

	// Los controles se crean una sola vez para toda la vida de la pantalla y se van pasando a los paneles, de forma que
	// las referencias de los componentes no cambian entre renders y el Form no se vuelve a montar
	const controls = useControlsFor<CustomerFormState>();
	const { Form } = controls;
	const formRef = React.useRef<FormRef<CustomerFormState>>(null);

	// El estado inicial se calcula una sola vez a partir del cliente que nos llega, mapeando el DTO al esquema del formulario
	const initialState = React.useMemo(() => toCustomerFormState(customer), [customer]);

	// País seleccionado. La antigua clase remontaba todo el formulario (updateModel + remountAll + forceUpdate) cuando
	// cambiaba el ComboBox de país para mostrar u ocultar el tipo de documento; aquí basta con escuchar el campo
	// con el onWatch del Select y guardar el valor en estado.
	const [countryCode, setCountryCode] = React.useState<string>(initialState.CountryCode);

	// Las promociones se editan con los botones del panel (Nuevo / Editar / Eliminar) en lugar de con el antiguo
	// EditableValueList, así que las mantenemos en estado para poder repintar la tabla y las volcamos al formulario
	// en cada cambio para que viajen en el modelo al validar y al guardar.
	const [offers, setOffers] = React.useState<CustomerOfferDTO[]>(initialState.Offers);
	const [selectedOffer, setSelectedOffer] = React.useState<Nullable<CustomerOfferDTO>>(null);


	const updateOffers = React.useCallback((updatedOffers: CustomerOfferDTO[]) => {
		setOffers(updatedOffers);
		formRef.current!.setValue('Offers', updatedOffers);
	}, []);


	// El diálogo de la promoción necesita el cliente para calcular las promociones que todavía están disponibles,
	// por eso le pasamos las promociones que hay en ese momento en el formulario.
	const customerWithCurrentOffers = React.useCallback((): CustomerForEditionDTO => ({ ...customer, Offers: offers }), [customer, offers]);


	const onSelectOffer = React.useCallback((offer: CustomerOfferDTO) => setSelectedOffer(offer), []);


	const onNewOffer = React.useCallback(async () => {
		const newOffer = { Offer: null!, MaxApplicationsPerBusinessDay: null } as CustomerOfferDTO;

		const result = await showEditCustomerOfferDialog(newOffer, customerWithCurrentOffers());
		if (!result.ok)
			return;

		updateOffers([...offers, result.value]);
		setSelectedOffer(result.value);
	}, [customerWithCurrentOffers, offers, updateOffers]);


	const onEditOffer = React.useCallback(async () => {
		if (selectedOffer === null)
			return;

		const result = await showEditCustomerOfferDialog(selectedOffer, customerWithCurrentOffers());
		if (!result.ok)
			return;

		updateOffers(offers.map(offer => offer === selectedOffer ? result.value : offer));
		setSelectedOffer(result.value);
	}, [customerWithCurrentOffers, offers, selectedOffer, updateOffers]);


	const onDeleteOffer = React.useCallback(async () => {
		if (selectedOffer === null)
			return;

		const confirmation = await Message.confirm(Res.Msg.MSG_CONFIRM_DELETION());
		if (!confirmation.ok)
			return;

		updateOffers(offers.filter(offer => offer !== selectedOffer));
		setSelectedOffer(null);
	}, [offers, selectedOffer, updateOffers]);


	// Validación en servidor, igual que el validator={CustomerFacade.validate} de la antigua clase. Los nombres de los
	// campos del esquema coinciden con los del DTO, así que los errores que devuelve el servidor se asignan solos
	// al campo correspondiente del formulario.
	const validateCustomer = React.useCallback(
		(state: CustomerFormState) => CustomerFacade.validate(toCustomerForEdition(customer, state)),
		[customer]);


	const onAccept = React.useCallback(async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		const customerToSave = toCustomerForEdition(customer, result.value);

		if (!(await checkDuplicates(customerToSave)))
			return;

		if (!(await userCanSetDocumentIdType(customerToSave)))
			return;

		const savedCustomer = await CustomerFacade.save(customerToSave);
		close(savedCustomer);
	}, [customer, close]);


	const onCancel = React.useCallback(async () => { cancel(); }, [cancel]);


	const promotionsParams: PromotionsPanelParams = {
		offers,
		selectedOffer,
		onSelectOffer,
		onNewOffer,
		onEditOffer,
		onDeleteOffer
	};

	// El tipo de documento sólo se pide cuando el país es el nuestro, exactamente igual que en la antigua clase
	const showDocumentIdType = countryCode === CountryNames.DEFAULT_CODE;

	const administrativeParams: AdministrativePanelParams = { controls, customer, showDocumentIdType, onCountryChanged: setCountryCode };


	// Lista con los identificadores de cada sección (pestaña) que se muestra en Phone/comandera.
	// Se usa useMemo para que el array se cree una sola vez y no cambie en cada render.
	// El "as const" permite que TypeScript infiera los valores exactos como tipo literal.
	const SEGMENT_KEYS = React.useMemo(() => ['adm', 'contact', 'location', 'terms', 'offers', 'notes'] as const, []);
	type SegmentKey = typeof SEGMENT_KEYS[number];

	// Pares clave/etiqueta que se pintan como pestañas en la barra superior.
	const segmentItems: { key: SegmentKey, label: string }[] = [
		{ key: 'adm', label: Res.Web.ADD_OR_EDIT_CUSTOMER_ADMINISTRATIVE_DATA_LBL() },
		{ key: 'contact', label: Res.Web.ADD_OR_EDIT_CUSTOMER_CONTACT_LBL() },
		{ key: 'location', label: Res.Web.ADD_OR_EDIT_CUSTOMER_LOCATION_LBL() },
		{ key: 'terms', label: Res.Web.ADD_OR_EDIT_CUSTOMER_COMMERCIAL_TERMS_LBL() },
		{ key: 'offers', label: Res.Web.ADD_OR_EDIT_CUSTOMER_OFFERS_LBL() },
		{ key: 'notes', label: Res.Web.ADD_OR_EDIT_CUSTOMER_NOTES_LBL() }
	];

	// Referencia al contenedor con scroll donde se muestran los paneles de cada sección.
	const panelScrollRef = React.useRef<HTMLDivElement>(null);
	// Referencias a cada panel (uno por sección) para poder hacer scroll hasta ellos.
	const panelRefs = React.useRef<Record<SegmentKey, HTMLDivElement | null>>({
		adm: null, contact: null, location: null, terms: null, offers: null, notes: null
	});
	// Referencias a cada pestaña de la barra superior para poder centrarlas al cambiar de sección.
	const segmentRefs = React.useRef<Record<SegmentKey, HTMLElement | null>>({
		adm: null, contact: null, location: null, terms: null, offers: null, notes: null
	});

	// Guarda la sección activa en una ref para poder consultarla desde callbacks sin provocar renders y en un estado con la pestaña activa.
	const activeSegmentRef = React.useRef<SegmentKey>('adm');
	const [activeSegment, setActiveSegment] = React.useState<SegmentKey>('adm');


	// Hace scroll horizontal en la barra de pestañas para dejar centrada la pestaña indicada.
	const centerSegment = React.useCallback((key: SegmentKey) => {
		segmentRefs.current[key]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
	}, []);


	// Se dispara al pulsar una pestaña: marca la sección como activa, desplaza el panel correspondiente
	// a la parte superior del contenedor y centra la pestaña en la barra.
	const onScrollIntoPanel = (key: SegmentKey, segmentEl: HTMLElement | null) => {
		activeSegmentRef.current = key;
		setActiveSegment(key);

		panelRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		segmentEl?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
	};


	// Esto es un efecto que sincroniza la pestaña activa con el scroll del usuario dentro de los paneles.
	// Usa IntersectionObserver para detectar qué panel está visible en el centro del contenedor
	// (rootMargin -45%/-45% define esa "franja central") y actualizar la pestaña resaltada.
	React.useEffect(() => {
		const container = panelScrollRef.current;
		if (!container)
			return;

		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				// Solo nos interesan los paneles que estén entrando en la franja visible.
				if (!entry.isIntersecting)
					return;

				// Recuperamos la clave de sección desde el atributo data-segment-key del panel.
				const key = entry.target.getAttribute('data-segment-key') as SegmentKey | null;
				// Si no hay clave o ya es la sección activa, no hacemos nada para evitar renders innecesarios.
				if (!key || activeSegmentRef.current === key)
					return;

				activeSegmentRef.current = key;
				setActiveSegment(key);
				centerSegment(key);
			});
		}, { root: container, rootMargin: '-45% 0px -45% 0px', threshold: 0 });

		// Registramos cada panel para que el observer vigile su visibilidad.
		SEGMENT_KEYS.forEach(key => {
			const el = panelRefs.current[key];
			if (el)
				observer.observe(el);
		});

		// Al desmontar el componente dejamos de observar para liberar recursos.
		return () => observer.disconnect();

	}, [centerSegment, SEGMENT_KEYS]);


	const newCustomer = customer.Id === 0;


	return (
		<PosScreen screenId={newCustomer ? 'ADD_CUSTOMER' : 'EDIT_CUSTOMER'} size={{ width: 1000, height: 720 }} useKeyPad={false} useKeyBoard={true}>
			<Header title={newCustomer ? Res.Web.ADD_CUSTOMER() : Res.Web.EDIT_CUSTOMER()} image={Images.SetCustomer} onClose={cancel} />
			<Content>
				<VStack width={'full'} height={'full'} paddingX={isPhone() ? '2' : '6'} paddingY={isPhone() ? '4' : '6'} bg={{ base: 'neutral.50', _dark: 'neutral.900' }}>
					<Form
						ref={formRef}
						initialValue={initialState}
						schema={customerFormSchema}
						validate={validateCustomer}
						style={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
					>
						<LargeDeviceOnly>
							<VStack width={'full'} flex={'1'} minH={'0'} gap={'6'} alignItems={'stretch'}>
								<VStack width={'full'} flex={'1'} minH={'0'} gap={'6'} overflowY={'auto'}>
									<HStack width={'full'} gap={'4'} padding={'1'} alignItems={'stretch'}>
										{administrativePanel(administrativeParams)}
										{contactPanel(controls)}
									</HStack>
									<HStack width={'full'} gap={'4'} padding={'1'} alignItems={'stretch'}>
										{locationPanel(controls)}
										{commercialTermsPanel(controls, customer)}
									</HStack>
									<HStack width={'full'} gap={'4'} padding={'1'} alignItems={'stretch'}>
										{promotionsPanel(promotionsParams)}
										{notesPanel(controls)}
									</HStack>
								</VStack>
								<HStack width={'full'} justifyContent={'center'}>
									<HStack width={'72'} height={'10'}>
										<BasicButton type={'success'} text={Res.Web.ADD_OR_EDIT_CUSTOMER_SAVE_LBL()} onClick={onAccept} width={'full'} height={'full'} />
									</HStack>
								</HStack>
							</VStack>
						</LargeDeviceOnly>
						<PhoneOnly>
							<VStack width={'full'} flex={'1'} minH={'0'} gap={'4'} alignItems={'stretch'}>
								<HStack width={'full'} minH={'10'} overflowY={'hidden'}>
									<SegmentGroup.Root value={activeSegment} onValueChange={(e) => setActiveSegment(e.value as SegmentKey)} borderRadius={'md'} shadow={'sm'} size={'md'}
										css={{
											'--segment-indicator-bg': 'var(--chakra-colors-primary)',
										}}
									>
										<SegmentGroup.Indicator />
										{segmentItems.map((item, index) => (
											<SegmentGroup.Item
												key={index}
												value={item.key}
												ref={(el: HTMLElement | null) => { segmentRefs.current[item.key] = el; }}
												onClick={(e) => onScrollIntoPanel(item.key, e.currentTarget)}
											>
												<SegmentGroup.ItemText _checked={{ color: 'white' }}>{item.label}</SegmentGroup.ItemText>
												<SegmentGroup.ItemHiddenInput />
											</SegmentGroup.Item>
										))}
									</SegmentGroup.Root>
								</HStack>
								<VStack ref={panelScrollRef} width={'full'} flex={'1'} gap={'6'} minH={'0'} overflowY={'auto'}>
									<Box width={'full'} data-segment-key={'adm'} ref={(el: HTMLDivElement | null) => { panelRefs.current.adm = el; }}>{administrativePanel(administrativeParams)}</Box>
									<Box width={'full'} data-segment-key={'contact'} ref={(el: HTMLDivElement | null) => { panelRefs.current.contact = el; }}>{contactPanel(controls)}</Box>
									<Box width={'full'} data-segment-key={'location'} ref={(el: HTMLDivElement | null) => { panelRefs.current.location = el; }}>{locationPanel(controls)}</Box>
									<Box width={'full'} data-segment-key={'terms'} ref={(el: HTMLDivElement | null) => { panelRefs.current.terms = el; }}>{commercialTermsPanel(controls, customer)}</Box>
									<Box width={'full'} data-segment-key={'offers'} ref={(el: HTMLDivElement | null) => { panelRefs.current.offers = el; }}>{promotionsPhonePanel(promotionsParams)}</Box>
									<Box width={'full'} data-segment-key={'notes'} ref={(el: HTMLDivElement | null) => { panelRefs.current.notes = el; }}>{notesPanel(controls)}</Box>
								</VStack>
							</VStack>
						</PhoneOnly>
					</Form>
				</VStack>
			</Content>
			<Footer>
				<PhoneOnly>
					<BackActionButton action={onCancel} />
					<Filler />
					<AcceptActionButton action={onAccept} />
				</PhoneOnly>
			</Footer>
		</PosScreen>
	);
}

declare global {
	interface CommandType {
		createCustomer: (initialPhone?: string) => Promise<Result<CustomerForEditionDTO>>
		editCustomer: (customerId: number) => Promise<Result<CustomerForEditionDTO>>
		deleteCustomer: (customerId: number) => Promise<Result>
	}
}

Command.register('editCustomer', Preconditions.ensure(Permission.Customer_CreateOrModify), async (customerId: number): Promise<Result<CustomerForEditionDTO>> => {
	const customer = await CustomerFacade.getForEdition(customerId);
	return Modal.show(AddOrEditCustomerScreen, { customer: customer });
});

Command.register('createCustomer', Preconditions.ensure(Permission.Customer_CreateOrModify), async (initialPhone?: string): Promise<Result<CustomerForEditionDTO>> => {
	const customer = await CustomerFacade.create();
	customer.Telephone = (initialPhone || '');
	return Modal.show(AddOrEditCustomerScreen, { customer: customer });
});

Command.register('deleteCustomer', Preconditions.ensure(Permission.Customer_Delete), async (customerId: number): Promise<Result> => {
	const result = await Message.confirm(Res.Msg.MSG_CONFIRM_DELETION());
	if (result.ok)
		await CustomerFacade.deleteCustomer(customerId);

	return result;
});
